import React, { useState, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { historicalScenarios } from '../data/comparisonData';
import LeadForm from './LeadForm';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPieChart, FiShield, FiTrendingUp, FiAlertCircle, FiArrowRight, FiTarget, FiActivity } = FiIcons;

const PortfolioComparisonPage = () => {
  const [initialValue, setInitialValue] = useState(100000);
  const [activeScenarioId, setActiveScenarioId] = useState(historicalScenarios[0].id);
  const [goldAlloc, setGoldAlloc] = useState(15);
  const [bondAlloc, setBondAlloc] = useState(25);
  
  const stockAlloc = 100 - goldAlloc - bondAlloc;

  const activeScenario = useMemo(() => 
    historicalScenarios.find(s => s.id === activeScenarioId) || historicalScenarios[0]
  , [activeScenarioId]);

  const calculations = useMemo(() => {
    const { stocks, bonds, gold } = activeScenario.data;
    
    const stockReturn = initialValue * (stocks / 100);
    const bondReturn = initialValue * (bonds / 100);
    const goldReturn = initialValue * (gold / 100);

    // 100% Stocks Portfolio
    const allStockValue = Math.round(initialValue + (initialValue * (stocks / 100)));

    // Traditional 60/40 Portfolio
    const tradValue = Math.round(
      (initialValue * 0.6 * (1 + stocks/100)) + 
      (initialValue * 0.4 * (1 + bonds/100))
    );

    // Custom Protected Portfolio
    const protectedValue = Math.round(
      (initialValue * (stockAlloc/100) * (1 + stocks/100)) +
      (initialValue * (bondAlloc/100) * (1 + bonds/100)) +
      (initialValue * (goldAlloc/100) * (1 + gold/100))
    );

    const drawdownMitigation = ((protectedValue - allStockValue) / initialValue * 100).toFixed(1);

    return {
      allStockValue,
      tradValue,
      protectedValue,
      drawdownMitigation
    };
  }, [initialValue, activeScenario, goldAlloc, bondAlloc, stockAlloc]);

  const chartOption = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '15%', containLabel: true },
    xAxis: { 
      type: 'category', 
      data: ['100% Stocks', 'Traditional 60/40', 'Your Custom Protected Portfolio'],
      axisLabel: { fontWeight: 'bold', color: '#1e293b', interval: 0 }
    },
    yAxis: { 
      type: 'value',
      axisLabel: { formatter: (value) => `$${(value / 1000).toFixed(0)}k` }
    },
    series: [{
      type: 'bar',
      barWidth: '50%',
      data: [
        { value: calculations.allStockValue, itemStyle: { color: '#94a3b8' } },
        { value: calculations.tradValue, itemStyle: { color: '#3b82f6' } },
        { value: calculations.protectedValue, itemStyle: { color: '#eab308' } }
      ],
      label: {
        show: true,
        position: 'top',
        formatter: (p) => `$${p.value.toLocaleString()}`,
        fontWeight: 'bold'
      }
    }]
  };

  return (
    <div className="pt-20 bg-slate-50 min-h-screen">
      {/* Header Section */}
      <section className="bg-slate-900 py-16 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-400 text-slate-900 rounded-full text-xs font-bold uppercase tracking-wider mb-6">
            <SafeIcon icon={FiActivity} />
            <span>Interactive Stress Test Tool</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">Portfolio Protection Simulator</h1>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
            Don't guess how your retirement would survive a crash. Use historical data to stress-test your current allocation against a gold-diversified strategy.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 mb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Controls Column */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-xl">
              <h3 className="text-xl font-bold text-slate-900 mb-8 flex items-center gap-2">
                <SafeIcon icon={FiTarget} className="text-yellow-500" />
                Simulator Inputs
              </h3>
              
              <div className="space-y-8">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-3">Portfolio Value ($)</label>
                  <input 
                    type="number" 
                    value={initialValue}
                    onChange={(e) => setInitialValue(Number(e.target.value))}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-yellow-500 font-bold text-lg"
                    step="10000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-3">Gold Allocation: {goldAlloc}%</label>
                  <input 
                    type="range" min="0" max="40" step="1" 
                    value={goldAlloc} 
                    onChange={(e) => setGoldAlloc(Number(e.target.value))}
                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-yellow-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-3">Bond Allocation: {bondAlloc}%</label>
                  <input 
                    type="range" min="0" max="60" step="1" 
                    value={bondAlloc} 
                    onChange={(e) => setBondAlloc(Number(e.target.value))}
                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                  />
                </div>

                <div className="p-4 bg-slate-900 rounded-2xl text-white">
                  <div className="flex justify-between items-center text-sm font-bold">
                    <span>Remaining (Stocks):</span>
                    <span className="text-yellow-400">{stockAlloc}%</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-xl">
              <h3 className="text-xl font-bold text-slate-900 mb-6">Select Crisis Scenario</h3>
              <div className="space-y-3">
                {historicalScenarios.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setActiveScenarioId(s.id)}
                    className={`w-full text-left p-4 rounded-2xl border transition-all ${
                      activeScenarioId === s.id 
                        ? 'bg-slate-900 border-slate-900 text-white shadow-lg' 
                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-400'
                    }`}
                  >
                    <div className="font-bold">{s.name}</div>
                    <div className={`text-xs ${activeScenarioId === s.id ? 'text-slate-400' : 'text-slate-500'}`}>{s.period}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Analysis Column */}
          <div className="lg:col-span-8 space-y-8">
            <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-xl">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-slate-900">{activeScenario.name}</h2>
                <p className="text-slate-600 mt-2">{activeScenario.description}</p>
              </div>
              <div className="h-[400px]">
                <ReactECharts option={chartOption} style={{ height: '100%', width: '100%' }} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-blue-600 p-6 rounded-3xl text-white shadow-lg">
                <SafeIcon icon={FiShield} className="text-3xl mb-4" />
                <div className="text-xs uppercase font-bold tracking-widest opacity-80">Portfolio Savings</div>
                <div className="text-3xl font-bold mt-1">${(calculations.protectedValue - calculations.allStockValue).toLocaleString()}</div>
                <div className="text-xs mt-2">Saved compared to 100% stocks</div>
              </div>
              <div className="bg-yellow-500 p-6 rounded-3xl text-slate-900 shadow-lg">
                <SafeIcon icon={FiTrendingUp} className="text-3xl mb-4" />
                <div className="text-xs uppercase font-bold tracking-widest opacity-80">Mitigation Power</div>
                <div className="text-3xl font-bold mt-1">+{calculations.drawdownMitigation}%</div>
                <div className="text-xs mt-2">Losses avoided by diversifying</div>
              </div>
              <div className="bg-slate-900 p-6 rounded-3xl text-white shadow-lg">
                <SafeIcon icon={FiAlertCircle} className="text-3xl mb-4 text-red-500" />
                <div className="text-xs uppercase font-bold tracking-widest opacity-80">Max Drawdown</div>
                <div className="text-3xl font-bold mt-1">-{Math.abs(Math.round((initialValue - calculations.protectedValue) / initialValue * 100))}%</div>
                <div className="text-xs mt-2">Total portfolio impact</div>
              </div>
            </div>

            <div className="bg-yellow-50 border-2 border-yellow-200 p-8 rounded-3xl">
              <h4 className="text-xl font-bold text-yellow-900 mb-4">The Expert Verdict</h4>
              <p className="text-yellow-800 text-lg leading-relaxed mb-6">
                {activeScenario.insight} By shifting just {goldAlloc}% into physical gold, you create a defensive layer that paper assets simply cannot provide.
              </p>
              <a href="#lead-form" className="inline-flex items-center justify-center px-8 py-4 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition-all">
                Learn How to Diversify <SafeIcon icon={FiArrowRight} className="ml-2" />
              </a>
            </div>

            <LeadForm 
              title="Get Your Free Wealth Protection Kit"
              description="See how the world's top investors use physical gold to hedge against the exact scenarios you just simulated."
              ctaText="Send My Free Guide"
            />
          </div>

        </div>
      </div>
    </div>
  );
};

export default PortfolioComparisonPage;